/**
 * Shared Candidates chrome for Table / Kanban / Dashboard.
 * Each surface passes a `features` flag object; view switcher lives only here.
 *
 * Table: [StatusTabs (overflow into "More") | +] left; search/actions right.
 * Kanban: create stage sits in right chrome. The row always uses `pt-3`;
 * Table also gets `pb-3` + bottom border (Kanban/Dashboard stay flush below).
 * Single line only: on a narrow row, search collapses to an icon.
 */

import { useState, type ReactNode } from 'react';
import { StatusTabs, type StatusTabItem } from '../status-tabs';
import {
  CandidatesViewSwitcher,
  type CandidatesPipelineView,
} from '../CandidatesViewSwitcher';
import { CandidatesToolbarSearch } from './CandidatesToolbarSearch';
import { cn } from '@/utils/cn';

export interface CandidatesToolbarFeatures {
  statusTabs: boolean;
  search: boolean;
  filters: boolean;
  sort: boolean;
  bulkActions: boolean;
  viewSwitcher: boolean;
}

export interface CandidatesToolbarProps {
  features: CandidatesToolbarFeatures;
  stageTabs?: StatusTabItem[];
  /** Keeps the Table tab layout (placeholder + create stage) while stages load. */
  isStageTabsLoading?: boolean;
  activeStageId?: string;
  onStageChange?: (stageId: string) => void;
  createStageSlot?: ReactNode;
  searchTerm?: string;
  onSearchTermChange?: (value: string) => void;
  onDebouncedSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  filtersSlot?: ReactNode;
  sortSlot?: ReactNode;
  bulkActionsSlot?: ReactNode;
  view?: CandidatesPipelineView;
  onViewChange?: (view: CandidatesPipelineView) => void;
  className?: string;
}

export function CandidatesToolbar({
  features,
  stageTabs,
  isStageTabsLoading = false,
  activeStageId,
  onStageChange,
  createStageSlot,
  searchTerm = '',
  onSearchTermChange = () => undefined,
  onDebouncedSearchChange,
  searchPlaceholder,
  filtersSlot,
  sortSlot,
  bulkActionsSlot,
  view,
  onViewChange,
  className,
}: CandidatesToolbarProps) {
  const showTabs =
    features.statusTabs &&
    Boolean(stageTabs?.length) &&
    Boolean(activeStageId) &&
    onStageChange != null;
  const showTabsPlaceholder =
    features.statusTabs && isStageTabsLoading && !showTabs;
  const showTabArea = showTabs || showTabsPlaceholder;
  const showCreateStage = Boolean(createStageSlot);
  const showSearch = features.search;
  const showFilters = features.filters && Boolean(filtersSlot);
  const showSort = features.sort && Boolean(sortSlot);
  const showBulk = features.bulkActions && Boolean(bulkActionsSlot);
  const showSwitcher =
    features.viewSwitcher && view != null && onViewChange != null;
  const createStageAfterTabs = showCreateStage && showTabArea;
  const createStageInRightChrome = showCreateStage && !showTabArea;
  const showRightChrome =
    showSearch ||
    showFilters ||
    showSort ||
    showBulk ||
    showSwitcher ||
    createStageInRightChrome;
  const isFlushChrome = view === 'kanban' || view === 'dashboard';
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  if (!showTabArea && !showRightChrome) {
    return null;
  }

  return (
    <div
      className={cn(
        'shrink-0 bg-light',
        !isFlushChrome && 'border-b border-light-lila/40',
        className,
      )}
    >
      <div
        className={cn(
          // `@container`: children adapt to the row width, not the viewport.
          '@container flex flex-row items-center gap-2 lg:gap-3 pl-4 pr-4 pt-3',
          !isFlushChrome && 'pb-3',
          !showTabArea && showRightChrome && 'justify-end',
        )}
      >
        {showTabArea ? (
          // Width comes from the row (never from the tabs), so StatusTabs can
          // measure it and move stages that do not fit into "More".
          <div
            className={cn(
              'flex min-w-0 flex-[1_1_16rem] items-center gap-2',
              isSearchExpanded && '@max-4xl:hidden',
            )}
          >
            {showTabs ? (
              <StatusTabs
                tabs={stageTabs!}
                activeId={activeStageId!}
                onChange={onStageChange!}
                trailing={createStageAfterTabs ? createStageSlot : undefined}
              />
            ) : (
              <>
                <div
                  aria-hidden
                  className="h-8 w-72 max-w-full rounded-full bg-light-lila/30 animate-pulse"
                />
                {createStageAfterTabs ? (
                  <div className="shrink-0">{createStageSlot}</div>
                ) : null}
              </>
            )}
          </div>
        ) : null}

        {showRightChrome ? (
          <div
            className={cn(
              'ml-auto flex shrink-0 items-center gap-2 @max-md:gap-1.5',
              isSearchExpanded && '@max-4xl:flex-1',
            )}
          >
            {showSearch ? (
              <CandidatesToolbarSearch
                value={searchTerm}
                onChange={onSearchTermChange}
                onDebouncedChange={onDebouncedSearchChange}
                placeholder={searchPlaceholder}
                isExpanded={isSearchExpanded}
                onExpandedChange={setIsSearchExpanded}
              />
            ) : null}
            {/* Open search on a narrow row takes over these (Kanban has no tabs). */}
            <div className={cn('contents', isSearchExpanded && '@max-4xl:hidden')}>
              {showFilters ? filtersSlot : null}
              {showSort ? sortSlot : null}
              {createStageInRightChrome ? createStageSlot : null}
              {showBulk ? bulkActionsSlot : null}
            </div>
            {showSwitcher ? (
              <CandidatesViewSwitcher value={view!} onValueChange={onViewChange!} />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
